/**
 * Header for the estimate workspace — shows the non-binding disclosure
 * (constitution Principle III, FR-013), the status pill, and the
 * project identity (room_label + room_type). Customers see an "Edit"
 * button that opens the IdentityEditor.
 */

import type { Estimate, EstimateStatus } from "@starz/shared/api/types.gen";
import { ROOM_TYPE_LABEL } from "@starz/shared/api/types.gen";

interface Props {
  estimate?: Estimate;
  status: EstimateStatus;
  createdAt?: string;
  isAdminView?: boolean;
  onEdit?: () => void;
}

const STATUS_LABEL: Record<EstimateStatus, string> = {
  in_progress: "In progress",
  submitted: "Submitted for review",
  in_review: "In review",
  info_requested: "Action needed",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
};

function projectTitle(estimate: Estimate | undefined): string {
  if (!estimate) return "Conversation with Stella";
  if (estimate.room_label) return estimate.room_label;
  if (estimate.room_type) return ROOM_TYPE_LABEL[estimate.room_type];
  return "Conversation with Stella";
}

export function EstimateHeader({ estimate, status, createdAt, isAdminView, onEdit }: Props) {
  const title = projectTitle(estimate);
  return (
    <header className="mb-6 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
        {isAdminView ? "Customer estimate" : "Your estimate"}
      </p>
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-serif text-3xl text-starz-navy md:text-4xl">{title}</h1>
        <span className="rounded-full border border-starz-gold/30 bg-starz-cream px-3 py-1 text-xs font-medium uppercase tracking-wider text-starz-charcoal">
          {STATUS_LABEL[status]}
        </span>
        {!isAdminView && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="ml-auto text-sm font-medium text-starz-gold hover:underline"
          >
            Edit project details
          </button>
        )}
      </div>
      <div className="h-0.5 w-14 bg-starz-gold" aria-hidden="true" />
      {estimate?.address && (
        <p className="text-sm text-warm-gray">
          {estimate.address.line1}
          {estimate.address.line2 ? `, ${estimate.address.line2}` : ""}, {estimate.address.city},{" "}
          {estimate.address.state} {estimate.address.zip}
        </p>
      )}
      <p className="max-w-prose text-sm text-warm-gray">
        Stella's responses are <strong>preliminary and non-binding</strong> until your contractor
        reviews the full estimate. She will never quote dimensions or dollar amounts directly —
        those come after photos are captured and the contractor approves.
      </p>
      {createdAt && (
        <p className="text-xs text-warm-gray">Started {new Date(createdAt).toLocaleString()}</p>
      )}
    </header>
  );
}
